<template>
  <div class="media-dialog" v-if="isVisible" v-loading="isBusy">
    <div class="media-dialog__header">
      <button @click="close" class="border-0 bg-transparent">
        <i class="fas fa-2x fa-times"></i>
      </button>
    </div>
    <div class="media-dialog__body">
      <auto-height>
        <el-col v-for="item in list" :key="item.model.id" :sm="10" :md="8" :lg="8" :xl="6">
          <item :key="item.model.id" :model="item.model" :is-selected="isSelected(item.model)"
                :update-url="item.updateUrl"
                :delete-url="item.deleteUrl" :can-delete="item.canDelete" :can-edit="item.canEdit" @select="onSelect" @delete="onDelete"></item>
        </el-col>
        <infinite-loading @infinite="loadItems" spinner="waveDots" :distance="500" :identifier="infiniteLoadKey">
          <template slot="no-more">
            <h5><span class="badge badge-secondary">No more records</span></h5>
          </template>
          <template slot="no-results">
            <h5><span class="badge badge-secondary">No results</span></h5>
          </template>
          <template slot="error" slot-scope="{ trigger }">
            <h5><span class="badge badge-danger">Whoops. It looks like the server is unavailable.</span>
            </h5>
            <div class="text-center">
              <el-button size="mini" @click="trigger()">Retry</el-button>
            </div>
          </template>
        </infinite-loading>
      </auto-height>
    </div>
    <div class="media-dialog__footer text-center">
      <button type="button" class="btn btn-primary" @click="addItems"><i class="fas fa-check"></i></button>
      <file-input @input="onFilesSelected" v-if="canCreate"></file-input>
    </div>
  </div>
</template>

<script>
import {addQueryParams} from "../../libs/url";
import Item from "./attachment-item";
import FileInput from "./attachment-file-input";
import AutoHeight from '../auto-height';

export default {
  components: {
    AutoHeight,
    Item,
    FileInput,
  },

  props: {
    indexMediaUrl: String,
    storeMediaUrl: String,
    canCreateMedia: Boolean,
  },

  data() {
    return {
      list: [],
      selected: [],
      visible: false,
      isBusy: false,
      limit: 50,
      currentLimit: 50,
      cancelToken: null,
      isRequestActive: false,
      infiniteLoadKey: null,
      max: 10,
    };
  },

  watch: {
    url(newUrl) {
      this.refresh();
    },
  },

  computed: {
    /**
     * @return {boolean}
     */
    isVisible() {
      return this.visible;
    },

    /**
     * @return {boolean}
     */
    canCreate() {
      return this.canCreateMedia;
    },
  },

  methods: {
    /**
     * @param {Object} $state
     * @return {undefined}
     */
    loadItems($state) {
      this.currentLimit = this.currentLimit + this.limit;
      this.performLoading($state);
    },

    /**
     * @param {Object|null} $state
     * @return {undefined}
     */
    performLoading($state) {
      if (this.isRequestActive) {
        this.cancelToken.cancel();
      } else {
        this.isRequestActive = true;
      }

      this.cancelToken = axios.CancelToken.source();

      axios.get(addQueryParams(this.indexMediaUrl, {limit: this.currentLimit}), {
        cancelToken: this.cancelToken.token
      })
          .then((res) => this.onLoadSuccess(res, $state))
          .catch(error => this.onLoadError(error, $state))
          .then((isCancelled) => {
            if (!isCancelled) {
              this.isRequestActive = false;
            }
          });
    },

    /**
     * @param {Object} data
     * @param {Object} $state
     * @return {Boolean}
     */
    onLoadSuccess({data: {data: result = []}}, $state) {
      if (_.size(result) <= _.size(this.list) || _.size(result) < this.list) {
        $state && $state.complete();
      }

      this.list = result;

      _.size(result) > 0 && $state && $state.loaded();

      return false;
    },

    /**
     * @param {Object} error
     * @param {Object} $state
     * @return {Boolean}
     */
    onLoadError(error, $state) {
      if (axios.isCancel(error)) {
        return true;
      }
      $state && $state.error();

      return false;
    },

    /**
     * @return {undefined}
     */
    show() {
      this.visible = true;
    },

    /**
     * @return {undefined}
     */
    close() {
      this.visible = false;
      this.refresh();
    },

    /**
     * @return {undefined}
     */
    refresh() {
      this.list = [];
      this.currentLimit = 50;
      this.selected = [];
      this.generateInfiniteLoadKey();
    },

    /**
     * @return {undefined}
     */
    generateInfiniteLoadKey() {
      this.infiniteLoadKey = this.url + moment().format('x');
    },

    /**
     * @param {Object}  item
     * @return {undefined}
     */
    onSelect(item) {
      let exist = _.findIndex(this.selected, function (media) {
        return media.id === item.id;
      });

      if (exist >= 0) {
        this.selected.splice(exist, 1);
      } else {
        if (_.size(this.selected) < this.max) {
          this.selected.push(item);
        } else {
          this.$message.error(`Twilio supports up to ${this.max} attachments`);
        }
      }
    },

    /**
     * @param {Object} item
     * @return {boolean}
     */
    isSelected(item) {
      let selected = _.find(this.selected, (media) => {
        return _.get(media, 'id') === _.get(item, 'id');
      });

      return !_.isEmpty(selected);
    },

    /**
     * @event add-media-items-to-message
     */
    addItems() {
      eventBus.$emit('add-media-items-to-message', this.selected);
      this.visible = false;
      this.refresh();
    },

    /**
     * @param {Object} files
     * @return {undefined}
     */
    onFilesSelected(files) {
      this.isBusy = true;

      const formData = new FormData();
      formData.append('_method', 'POST');
      _.each(files, (file) => {
        formData.append('files[]', file);
      });

      axios.post(this.storeMediaUrl, formData, {})
          .then(({data: {message}}) => {
            _.isString(message) && this.$message.success(message);
          })
          .catch(({response: {data, status}}) => {
            const {errors, message} = data || {};
            _.isString(message) && this.$message.error(message);
          })
          .then((isBusy = false) => {
            this.isBusy = isBusy;
            this.refresh();
          });
    },

    /**
     * @param {string} url
     */
    onDelete({url}) {
      this.isBusy = true;
      axios.post(url, {_method: 'DELETE'})
          .then(({data: {message}}) => {
            _.isString(message) && this.$message.success(message);
          })
          .catch((err) => {
            const message = _.get(err, 'response.data.message');
            _.isString(message) && this.$message.error(message);
          })
          .then((isBusy = false) => {
            this.isBusy = isBusy;
            this.refresh();
          });
    },
  },

  created() {
    eventBus.$on('show-attachment-dialog', this.show);
    eventBus.$on('refresh-attachment-list', this.refresh);
  },

  beforeDestroy() {
    eventBus.$off('show-attachment-dialog', this.show);
    eventBus.$off('refresh-attachment-list', this.refresh);
  },
}
</script>

<style scoped>

</style>
